import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  of,
  Subscription,
  timer,
  defer,
  forkJoin
} from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { Tag, AddTagRequest } from '@shared/models/tag';
import { OpportunityApiHttp } from '@core/api/opportunity-api-http';
import { ApiHttp } from '@core/api/api-http';
import { ClaimsService } from '@core/authentication/claims.service';
import { MonitoringService } from '@core/services/monitoring.service';
import { environment } from '@env/environment';

const TAG_LIBRARY_CACHE_STORAGE_PREFIX = 'spark_tag_cache_';
const TAG_LIBRARY_MONITORING_SOURCE = 'TagService';
const TAGS_API_SEGMENT = 'tags';
const TAG_LIBRARY_LOAD_ERROR_PREFIX = 'Failed to load tags from Management API';

export enum TagEntityType {
  Opportunities = 'opportunities',
  Individuals = 'individuals',
  Companies = 'companies',
  Trusts = 'trusts',
  Leads = 'leads'
}

export enum TagFilterScope {
  OpportunitiesOnly = 'opportunities',
  ClientsOnly = 'clients',
  All = 'all'
}

/** GET Clients API tags/entities — contact rows from ContactSummary (entityType = Individual | Company | Trust, etc.). */
export interface TaggedContact {
  entityId: string;
  entityType: string;
}

/** Clients service GET .../tags/entities JSON body (requires entityType query). */
export interface ClientsTaggedEntitiesResponse {
  contacts: TaggedContact[];
  leads: string[];
}

/** Merged targets for filtering consolidated tasks by tag (opportunities + clients contacts + leads). */
export interface TagFilterTargets {
  opportunityIds: string[];
  taggedContacts: TaggedContact[];
  leadIds: string[];
}

function emptyTagFilterTargets(): TagFilterTargets {
  return { opportunityIds: [], taggedContacts: [], leadIds: [] };
}

interface TagCacheEntry {
  tags: Tag[];
  lastFetched: number;
}

@Injectable({
  providedIn: 'root'
})
export class TagService implements OnDestroy {
  private readonly CACHE_TTL = 30 * 60 * 1000;

  private tagCache$ = new BehaviorSubject<Tag[] | null>(null);
  private refreshTimer$?: Subscription;
  private tagLibraryRefCount = 0;

  constructor(
    private opportunityHttp: OpportunityApiHttp,
    private apiHttp: ApiHttp,
    private claimsService: ClaimsService,
    private monitoringService: MonitoringService
  ) {}

  ngOnDestroy(): void {
    this.stopRefreshTimer();
  }

  private stopRefreshTimer(): void {
    this.refreshTimer$?.unsubscribe();
    this.refreshTimer$ = undefined;
  }

  private getTagLibrarySessionStorageKey(): string | null {
    const userId = this.claimsService.userId;
    if (!userId) {
      return null;
    }
    return `${TAG_LIBRARY_CACHE_STORAGE_PREFIX}${userId}`;
  }

  private removeOtherUsersTagLibraryKeys(currentUserKey: string): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          key.startsWith(TAG_LIBRARY_CACHE_STORAGE_PREFIX) &&
          key !== currentUserKey
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch {
      /* ignore */
    }
  }

  private computeRefreshDelayMs(): number | null {
    if (this.tagCache$.value === null) {
      return null;
    }
    const storageKey = this.getTagLibrarySessionStorageKey();
    if (!storageKey) {
      return this.CACHE_TTL;
    }
    const stored = sessionStorage.getItem(storageKey);
    if (stored) {
      try {
        const entry: TagCacheEntry = JSON.parse(stored);
        const age = Date.now() - entry.lastFetched;
        if (age < this.CACHE_TTL) {
          return this.CACHE_TTL - age;
        }
      } catch {
        /* ignore */
      }
    }
    return this.CACHE_TTL;
  }

  private ensureRefreshTimerIfNeeded(): void {
    if (this.tagLibraryRefCount < 1 || this.refreshTimer$) {
      return;
    }
    const delayMs = this.computeRefreshDelayMs();
    if (delayMs !== null) {
      this.startRefreshTimer(delayMs);
    }
  }

  private withTagLibrarySubscribers<T>(source: Observable<T>): Observable<T> {
    return defer(() => {
      this.tagLibraryRefCount++;
      this.ensureRefreshTimerIfNeeded();
      return source;
    }).pipe(
      finalize(() => {
        this.tagLibraryRefCount--;
        if (this.tagLibraryRefCount === 0) {
          this.stopRefreshTimer();
        }
      })
    );
  }

  private deduplicateByTagName(tags: Tag[]): Tag[] {
    const userPortfolio = this.claimsService.portfolioId;
    const grouped = new Map<string, Tag[]>();
    for (const tag of tags) {
      const key = tag.tagName.toLowerCase();
      const group = grouped.get(key) || [];
      group.push(tag);
      grouped.set(key, group);
    }
    return Array.from(grouped.values())
      .map((group) => {
        if (group.length === 1) return group[0];
        return group.find((t) => t.portfolio === userPortfolio) || group[0];
      })
      .sort((a, b) => a.tagName.localeCompare(b.tagName));
  }

  private isClientsEntity(entityType: TagEntityType): boolean {
    return entityType !== TagEntityType.Opportunities;
  }

  private getCachedTags(): Observable<Tag[]> {
    if (this.tagCache$.value !== null) {
      return of(this.tagCache$.value);
    }

    const storageKey = this.getTagLibrarySessionStorageKey();
    if (storageKey) {
      this.removeOtherUsersTagLibraryKeys(storageKey);
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        try {
          const entry: TagCacheEntry = JSON.parse(stored);
          const age = Date.now() - entry.lastFetched;
          if (age < this.CACHE_TTL) {
            this.tagCache$.next(entry.tags);
            if (this.tagLibraryRefCount > 0) {
              this.startRefreshTimer(this.CACHE_TTL - age);
            }
            return of(entry.tags);
          }
        } catch {
          /* corrupted storage, fall through to fetch */
        }
      }
    }

    return this.fetchAndCacheTags();
  }

  private logTagLibraryLoadFailure(
    error: unknown,
    operation: 'initial' | 'refresh'
  ): void {
    const wrapped =
      error instanceof Error
        ? error
        : new Error(`${TAG_LIBRARY_LOAD_ERROR_PREFIX} (${operation})`);
    const props: { [key: string]: string | number } = {
      source: TAG_LIBRARY_MONITORING_SOURCE,
      operation,
      managementApiUrl: environment.managementApiUrl
    };
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: unknown }).status;
      if (typeof status === 'number') {
        props.httpStatus = status;
      }
    }
    this.monitoringService.logException(wrapped, 3, props);
  }

  private fetchAndCacheTags(): Observable<Tag[]> {
    return this.apiHttp
      .get<Tag[]>(
        environment.managementApiUrl,
        TAGS_API_SEGMENT,
        ApiHttp.getStandardOptions()
      )
      .pipe(
        map((tags) => this.deduplicateByTagName(tags)),
        tap((tags) => {
          this.tagCache$.next(tags);
          this.persistToStorage(tags);
          if (this.tagLibraryRefCount > 0) {
            this.startRefreshTimer(this.CACHE_TTL);
          }
        }),
        catchError((error) => {
          this.logTagLibraryLoadFailure(error, 'initial');
          return of(this.tagCache$.value ?? []);
        })
      );
  }

  private persistToStorage(tags: Tag[]): void {
    const storageKey = this.getTagLibrarySessionStorageKey();
    if (!storageKey) {
      return;
    }
    try {
      this.removeOtherUsersTagLibraryKeys(storageKey);
      const entry: TagCacheEntry = { tags, lastFetched: Date.now() };
      sessionStorage.setItem(storageKey, JSON.stringify(entry));
    } catch {
      /* storage full or unavailable */
    }
  }

  private addToCache(tag: Tag): void {
    const current = this.tagCache$.value ?? [];
    if (!current.some((t) => t.tagId === tag.tagId)) {
      const updated = [...current, tag].sort((a, b) =>
        a.tagName.localeCompare(b.tagName)
      );
      this.tagCache$.next(updated);
      this.persistToStorage(updated);
    }
  }

  private startRefreshTimer(delayMs: number): void {
    this.refreshTimer$?.unsubscribe();
    this.refreshTimer$ = timer(delayMs, this.CACHE_TTL)
      .pipe(
        switchMap(() =>
          this.apiHttp
            .get<Tag[]>(
              environment.managementApiUrl,
              TAGS_API_SEGMENT,
              ApiHttp.getStandardOptions()
            )
            .pipe(
              catchError((error) => {
                this.logTagLibraryLoadFailure(error, 'refresh');
                return of(this.tagCache$.value ?? []);
              })
            )
        ),
        map((tags) => this.deduplicateByTagName(tags)),
        tap((tags) => {
          this.tagCache$.next(tags);
          this.persistToStorage(tags);
        })
      )
      .subscribe();
  }

  getAllTags(): Observable<Tag[]> {
    return this.withTagLibrarySubscribers(this.getCachedTags());
  }

  searchTags(query: string): Observable<Tag[]> {
    return this.withTagLibrarySubscribers(this.getCachedTags()).pipe(
      map((tags) => {
        const term = query.trim().toLowerCase();
        if (!term) return tags;
        return tags.filter((t) => t.tagName.toLowerCase().includes(term));
      })
    );
  }

  createTag(tagName: string): Observable<Tag> {
    return this.apiHttp
      .post<Tag>(
        environment.managementApiUrl,
        TAGS_API_SEGMENT,
        { tagName },
        ApiHttp.getStandardOptions()
      )
      .pipe(tap((tag) => this.addToCache(tag)));
  }

  /**
   * Returns an existing tag from the in-memory/session tag library when the name matches (case-insensitive),
   * otherwise creates the tag via the management API.
   */
  getTagFromCacheOrCreate(tagName: string): Observable<Tag> {
    const normalized = tagName.trim();
    return this.getAllTags().pipe(
      switchMap((tags) => {
        const existing = tags.find(
          (t) => t.tagName.toLowerCase() === normalized.toLowerCase()
        );
        if (existing) {
          return of(existing);
        }
        return this.createTag(normalized);
      })
    );
  }

  getEntityTags(
    entityType: TagEntityType,
    entityId: string
  ): Observable<Tag[]> {
    const path = `${entityType}/${entityId}/${TAGS_API_SEGMENT}`;
    if (this.isClientsEntity(entityType)) {
      return this.apiHttp.get<Tag[]>(
        environment.clientsApiUrl,
        path,
        ApiHttp.getStandardOptions()
      );
    }
    return this.opportunityHttp.get<Tag[]>(path, ApiHttp.getStandardOptions());
  }

  addTagToEntity(
    entityType: TagEntityType,
    entityId: string,
    request: AddTagRequest
  ): Observable<any> {
    const path = `${entityType}/${entityId}/${TAGS_API_SEGMENT}`;
    if (this.isClientsEntity(entityType)) {
      return this.apiHttp.post(
        environment.clientsApiUrl,
        path,
        request,
        ApiHttp.getStandardOptions()
      );
    }
    return this.opportunityHttp.post(
      path,
      request,
      ApiHttp.getStandardOptions()
    );
  }

  addTagsToEntity(
    entityType: TagEntityType,
    entityId: string,
    requests: AddTagRequest[]
  ): Observable<any> {
    const path = `${entityType}/${entityId}/${TAGS_API_SEGMENT}`;
    if (this.isClientsEntity(entityType)) {
      return this.apiHttp.post(
        environment.clientsApiUrl,
        path,
        requests,
        ApiHttp.getStandardOptions()
      );
    }
    return this.opportunityHttp.post(
      path,
      requests,
      ApiHttp.getStandardOptions()
    );
  }

  removeTagFromEntity(
    entityType: TagEntityType,
    entityId: string,
    tagId: string
  ): Observable<any> {
    const path = `${entityType}/${entityId}/${TAGS_API_SEGMENT}/${tagId}`;
    if (this.isClientsEntity(entityType)) {
      return this.apiHttp.delete(environment.clientsApiUrl, path);
    }
    return this.opportunityHttp.delete(path);
  }

  getTaskTags(
    entityType: TagEntityType,
    entityId: string,
    taskId: string
  ): Observable<Tag[]> {
    const path = `${entityType}/${entityId}/tasks/${taskId}/${TAGS_API_SEGMENT}`;
    if (this.isClientsEntity(entityType)) {
      return this.apiHttp.get<Tag[]>(
        environment.clientsApiUrl,
        path,
        ApiHttp.getStandardOptions()
      );
    }
    return this.opportunityHttp.get<Tag[]>(path, ApiHttp.getStandardOptions());
  }

  addTagToTask(
    entityType: TagEntityType,
    entityId: string,
    taskId: string,
    request: AddTagRequest
  ): Observable<any> {
    const path = `${entityType}/${entityId}/tasks/${taskId}/${TAGS_API_SEGMENT}`;
    if (this.isClientsEntity(entityType)) {
      return this.apiHttp.post(
        environment.clientsApiUrl,
        path,
        request,
        ApiHttp.getStandardOptions()
      );
    }
    return this.opportunityHttp.post(
      path,
      request,
      ApiHttp.getStandardOptions()
    );
  }

  addTagsToTask(
    entityType: TagEntityType,
    entityId: string,
    taskId: string,
    requests: AddTagRequest[]
  ): Observable<any> {
    const path = `${entityType}/${entityId}/tasks/${taskId}/${TAGS_API_SEGMENT}`;
    if (this.isClientsEntity(entityType)) {
      return this.apiHttp.post(
        environment.clientsApiUrl,
        path,
        requests,
        ApiHttp.getStandardOptions()
      );
    }
    return this.opportunityHttp.post(
      path,
      requests,
      ApiHttp.getStandardOptions()
    );
  }

  removeTagFromTask(
    entityType: TagEntityType,
    entityId: string,
    taskId: string,
    tagId: string
  ): Observable<any> {
    const path = `${entityType}/${entityId}/tasks/${taskId}/${TAGS_API_SEGMENT}/${tagId}`;
    if (this.isClientsEntity(entityType)) {
      return this.apiHttp.delete(environment.clientsApiUrl, path);
    }
    return this.opportunityHttp.delete(path);
  }

  getOpportunityTags(opportunityId: string): Observable<Tag[]> {
    return this.getEntityTags(TagEntityType.Opportunities, opportunityId);
  }

  addTagToOpportunity(
    opportunityId: string,
    request: AddTagRequest
  ): Observable<any> {
    return this.addTagToEntity(
      TagEntityType.Opportunities,
      opportunityId,
      request
    );
  }

  removeTagFromOpportunity(
    opportunityId: string,
    tagId: string
  ): Observable<any> {
    return this.removeTagFromEntity(
      TagEntityType.Opportunities,
      opportunityId,
      tagId
    );
  }

  /**
   * Resolves entities that have the given tag IDs. Clients API requires entityType=All for both contacts and leads.
   */
  getTagFilterTargets(
    tagIds: string[],
    scope: TagFilterScope
  ): Observable<TagFilterTargets> {
    if (!tagIds.length) {
      return of(emptyTagFilterTargets());
    }

    const tagIdsParam = tagIds.join(',');
    const clientsTaggedEntitiesPath = `${TAGS_API_SEGMENT}/entities?tagIds=${tagIdsParam}&entityType=All`;

    if (scope === TagFilterScope.OpportunitiesOnly) {
      return this.opportunityHttp
        .get<{ opportunityIds: string[] }>(
          `${TAGS_API_SEGMENT}/opportunity-ids?tagIds=${tagIdsParam}`,
          ApiHttp.getStandardOptions()
        )
        .pipe(
          map((r) => ({
            ...emptyTagFilterTargets(),
            opportunityIds: r.opportunityIds
          }))
        );
    }

    if (scope === TagFilterScope.ClientsOnly) {
      return this.apiHttp
        .get<ClientsTaggedEntitiesResponse>(
          environment.clientsApiUrl,
          clientsTaggedEntitiesPath,
          ApiHttp.getStandardOptions()
        )
        .pipe(
          map((r) => ({
            opportunityIds: [],
            taggedContacts: r.contacts ?? [],
            leadIds: r.leads ?? []
          })),
          catchError(() => of(emptyTagFilterTargets()))
        );
    }

    return forkJoin([
      this.opportunityHttp
        .get<{ opportunityIds: string[] }>(
          `${TAGS_API_SEGMENT}/opportunity-ids?tagIds=${tagIdsParam}`,
          ApiHttp.getStandardOptions()
        )
        .pipe(
          map((r) => r.opportunityIds),
          catchError(() => of([] as string[]))
        ),
      this.apiHttp
        .get<ClientsTaggedEntitiesResponse>(
          environment.clientsApiUrl,
          clientsTaggedEntitiesPath,
          ApiHttp.getStandardOptions()
        )
        .pipe(
          map((r) => ({
            contacts: r.contacts ?? [],
            leads: r.leads ?? []
          })),
          catchError(() =>
            of({ contacts: [] as TaggedContact[], leads: [] as string[] })
          )
        )
    ]).pipe(
      map(([oppIds, clients]) => ({
        opportunityIds: oppIds,
        taggedContacts: clients.contacts,
        leadIds: clients.leads
      }))
    );
  }
}
